/**
 * Toolbox Card Component
 * Displays a toolbox card with approved role badge
 * Client component to handle role checking
 */

"use client"

import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useUser } from "@/contexts/UserContext"
import { useApprovedRoles } from "@/hooks/useApprovedRoles"
import { cn } from "@/lib/utils"
import type { ThemeRoute } from "@/utils/themeColors"
import { getThemeBackgroundColor, getThemeTextColor } from "@/utils/themeColors"
import { ArrowRight, CheckCircle2, Lock, Sparkles } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useMemo } from "react"

interface ToolboxCardProps {
  id: number
  title: string
  description: string
  link: string
  requiredRole: string
  themeColor: ThemeRoute
}

/**
 * Format role name (editor_restrail -> Editor Restrail)
 */
function formatRoleName(role: string): string {
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Normalize role for comparison (case-insensitive)
 * Same logic as RoleGuard
 */
function normalizeRole(role: string): string {
  return role.toLowerCase().trim().replace(/\s+/g, "_")
}

export default function ToolboxCard({
  id,
  title,
  description,
  link,
  requiredRole,
  themeColor,
}: ToolboxCardProps) {
  const router = useRouter()
  const user = useUser()
  const isAuthenticated = !!user.email

  // Only fetch roles if user is authenticated
  const { approvedRoles, loading } = useApprovedRoles({
    enabled: isAuthenticated,
  })

  // Get theme colors
  const themeIconColor = getThemeTextColor(themeColor)
  const themeBgClass = getThemeBackgroundColor(themeColor)

  // Check if user has the required role using the same logic as RoleGuard
  const hasRole = useMemo(() => {
    if (!isAuthenticated) {
      return false
    }

    if (loading) {
      return false // Still loading, can't determine yet
    }

    // Normalize roles for comparison (same as RoleGuard)
    const normalizedRequiredRole = normalizeRole(requiredRole)
    const normalizedApprovedRoles = approvedRoles.map(normalizeRole)

    // Check exact match first, then normalized match
    const exactMatch = approvedRoles.includes(requiredRole)
    const normalizedMatch = normalizedApprovedRoles.includes(
      normalizedRequiredRole
    )

    return exactMatch || normalizedMatch
  }, [isAuthenticated, loading, approvedRoles, requiredRole])

  // Card is disabled if:
  // - User is not authenticated, OR
  // - Loading is complete and user doesn't have the required role
  const isDisabled = !isAuthenticated || (!loading && !hasRole)

  const cardContent = (
    <Card
      className={cn(
        "h-full border-transparent rounded bg-slate-500/5 transition-all duration-300",
        isDisabled
          ? "opacity-60 cursor-not-allowed bg-muted/50"
          : "hover:shadow-lg hover:shadow-primary/5 group-hover:border-primary/50"
      )}
    >
      <CardHeader className="pb-1">
        <div className="mb-4 flex h-20 items-center justify-center relative">
          <div
            className={cn(
              "flex h-16 w-16 items-center justify-center rounded-full transition-transform",
              themeBgClass,
              !isDisabled && "group-hover:scale-110"
            )}
          >
            <Sparkles className={cn("h-8 w-8", themeIconColor)} />
          </div>
          {isDisabled && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-background/90 rounded-full p-3 shadow-lg">
                <Lock className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-start justify-between gap-2">
          <CardTitle
            className={cn(
              "text-xl transition-colors flex-1",
              isDisabled ? "text-muted-foreground" : "group-hover:text-primary"
            )}
          >
            {title}
          </CardTitle>
          {/* Approved Role Badge */}
          {isAuthenticated && !loading && hasRole && (
            <Badge
              variant="outline"
              className="text-xs px-2 py-0.5 text-green-500 shrink-0"
              title="Granted Access"
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />
              <span className="sr-only"> {formatRoleName(requiredRole)}</span>
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <CardDescription className="text-sm leading-relaxed">
          {description}
        </CardDescription>
        {!isDisabled && (
          <div className="flex items-center gap-2 text-sm font-medium text-primary group-hover:gap-3 transition-all">
            Find out more
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </CardContent>
    </Card>
  )

  // If disabled, return card with tooltip explaining why
  if (isDisabled) {
    const formattedRole = formatRoleName(requiredRole)

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group w-full">{cardContent}</div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-2">
            <p className="font-medium">
              You do not have the required role to access this toolbox.
            </p>
            <p className="text-sm">
              Required role:{" "}
              <span className="font-semibold">{formattedRole}</span>
            </p>
            <Link
              href="/request/access"
              className="text-sm hover:underline font-medium inline-flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              Request access
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </TooltipContent>
      </Tooltip>
    )
  }

  // If enabled, wrap in link
  return (
    <Link href={link} className="group">
      {cardContent}
    </Link>
  )
}
